import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { Calendar, CheckCircle, Clock, XCircle, Video, ClipboardList, RotateCcw, Ban, CalendarPlus } from 'lucide-react'
import { interviewerApi, type ChangeRequestItem } from '../../lib/api/interviewer'
import { friendlyApiError } from '../../lib/utils/apiError'
import { encodeId } from '../../lib/utils/idObfuscation'
import { buildInterviewerIcs, downloadIcs } from '../../lib/utils/ics'
import {
  UNATTENDED_REASON,
  attendanceHint,
  isNoShowState,
  isUnattendedState,
  noShowAllowsEvaluation,
  noShowWording,
  type AttendanceState,
} from '../../lib/utils/attendance'

interface DashboardStats {
  upcoming: number
  today: number
  completed: number
  cancelled: number
  no_show: number
  pending_evaluations: number
  avg_rating: number | null
}

interface InterviewItem {
  id: number
  client_id: number | null
  client_name: string | null
  client_email: string | null
  title: string | null
  scheduled_at: string
  duration_minutes: number
  status: string
  zoom_join_url: string | null
  zoom_host_url: string | null
  zoom_password: string | null
  target_role: string | null
  evaluation_id: number | null
  evaluation_status: string | null
  source: string
  cancellation_reason: string | null
  no_show_party: string | null
  attendance_state: AttendanceState
  calendar_uid: string | null
  calendar_sequence: number
}

type FilterType = 'all' | 'upcoming' | 'completed' | 'cancelled' | 'no_show'

function parseDate(iso: string, durationMinutes: number, tz?: string) {
  const d = new Date(iso)
  const now = new Date()
  const endTime = new Date(d.getTime() + durationMinutes * 60 * 1000)
  const tzOpts = tz ? { timeZone: tz } : {}
  const isToday = tz
    ? new Intl.DateTimeFormat('en-US', { timeZone: tz, dateStyle: 'short' }).format(d) ===
      new Intl.DateTimeFormat('en-US', { timeZone: tz, dateStyle: 'short' }).format(now)
    : d.toDateString() === now.toDateString()
  return {
    d,
    date: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', ...tzOpts }),
    time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, ...tzOpts }),
    hasStarted: d <= now,           // meeting start time has passed
    hasEnded: endTime <= now,        // meeting start + duration has passed
    isInProgress: d <= now && endTime > now, // currently in the meeting window
    isToday,
  }
}


export default function InterviewerHome() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [stats, setStats] = useState<DashboardStats>({
    upcoming: 0,
    today: 0,
    completed: 0,
    cancelled: 0,
    no_show: 0,
    pending_evaluations: 0,
    avg_rating: null,
  })
  const [interviews, setInterviews] = useState<InterviewItem[]>([])
  const [filter, setFilter] = useState<FilterType>('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [interviewerTz, setInterviewerTz] = useState<string | undefined>(undefined)
  // bookingId → true if at least one question set has been saved for this booking
  const [prepDoneMap, setPrepDoneMap] = useState<Record<number, boolean>>({})

  // Change requests the interviewer has raised. Interviewers can NEVER
  // self-reschedule/cancel a booked session — they raise a request that an admin
  // reviews. The client is only notified once the admin actually reschedules or
  // cancels the session (that dispatches the client email server-side).
  const [changeRequests, setChangeRequests] = useState<ChangeRequestItem[]>([])
  const [crModal, setCrModal] = useState<{ item: InterviewItem; type: 'reschedule' | 'cancel' } | null>(null)
  const [crReason, setCrReason] = useState('')
  const [crSubmitting, setCrSubmitting] = useState(false)

  const loadChangeRequests = async () => {
    try {
      setChangeRequests(await interviewerApi.getMyChangeRequests())
    } catch {
      /* non-fatal: the buttons still work, we just can't show pending state */
    }
  }

  useEffect(() => {
    loadChangeRequests()
  }, [])

  const submitChangeRequest = async () => {
    if (!crModal) return
    setCrSubmitting(true)
    try {
      await interviewerApi.requestBookingChange(crModal.item.id, {
        type: crModal.type,
        reason: crReason.trim() || undefined,
      })
      toast.success(
        crModal.type === 'cancel'
          ? 'Cancellation request sent — an admin will review it.'
          : 'Reschedule request sent — an admin will review it.',
      )
      setCrModal(null)
      setCrReason('')
      await loadChangeRequests()
    } catch (err) {
      toast.error(friendlyApiError(err, 'Failed to send the request.'))
    } finally {
      setCrSubmitting(false)
    }
  }

  useEffect(() => {
    const load = async () => {
      try {
        // Fetch profile and dashboard in parallel so timezone is available before processing items
        const [profileRes, data] = await Promise.all([
          interviewerApi.getProfile().catch(() => null),
          interviewerApi.getDashboard(),
        ])
        const p = profileRes?.data ?? profileRes
        const tz: string | undefined = p?.timezone ?? undefined
        if (tz) setInterviewerTz(tz)

        setStats({
          upcoming: data.stats?.upcoming ?? 0,
          today: data.stats?.today ?? 0,
          completed: data.stats?.completed ?? 0,
          cancelled: data.stats?.cancelled ?? 0,
          no_show: data.stats?.no_show ?? 0,
          pending_evaluations: data.stats?.pending_evaluations ?? 0,
          avg_rating: data.stats?.avg_rating ?? null,
        })
        const items: InterviewItem[] = data.upcoming_interviews ?? []
        setInterviews(items)

        // Fetch question sets for each unique client to determine prep status.
        // Virtual session_id for a booking = booking_id + 100000.
        // Use tz directly (not state) so the correct timezone is used on first render.
        const upcomingItems = items.filter((it) => {
          const { hasEnded } = parseDate(it.scheduled_at, it.duration_minutes, tz)
          return !hasEnded && it.client_id && it.status !== 'completed' && it.status !== 'cancelled'
        })
        const uniqueClientIds = [...new Set(upcomingItems.map((it) => it.client_id!))]
        if (uniqueClientIds.length === 0) return

        const results = await Promise.all(
          uniqueClientIds.map((cid) =>
            interviewerApi.getClientQuestionSets(cid).catch(() => [] as { session_id: number }[]),
          ),
        )
        // Build a set of virtual session IDs that have question sets
        const prepSessionIds = new Set<number>()
        results.flat().forEach((qs: { session_id: number }) => {
          if (qs.session_id) prepSessionIds.add(qs.session_id)
        })
        // Map bookingId → prep done (virtual session_id = bookingId + 100000)
        const map: Record<number, boolean> = {}
        upcomingItems.forEach((it) => {
          map[it.id] = prepSessionIds.has(it.id + 100000)
        })
        setPrepDoneMap(map)
      } catch {
        setError('Failed to load dashboard data.')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  function openEvaluation(item: InterviewItem) {
    navigate(`/interviewer/evaluations?bookingId=${item.id}`)
  }

  // Build and download an interviewer-side .ics on the fly from the booking
  // record. Available while the session is still upcoming, so the interviewer
  // can add it to (or re-add it to) their personal calendar at any time.
  function handleDownloadIcs(item: InterviewItem) {
    try {
      const ics = buildInterviewerIcs({
        bookingId: item.id,
        clientName: item.client_name,
        clientId: item.client_id,
        title: item.title,
        targetRole: item.target_role,
        scheduledStartUtc: item.scheduled_at,
        durationMinutes: item.duration_minutes,
        zoomUrl: item.zoom_host_url ?? item.zoom_join_url,
        zoomPassword: item.zoom_password,
        timezone: interviewerTz,
        calendarUid: item.calendar_uid,
        calendarSequence: item.calendar_sequence,
      })
      downloadIcs(`interview-${item.id}.ics`, ics)
    } catch {
      toast.error('Could not generate the calendar file.')
    }
  }

  const filtered = interviews.filter((item) => {
    const { hasEnded } = parseDate(item.scheduled_at, item.duration_minutes, interviewerTz)
    // Cancelled is its own bucket — a cancelled booking is never "upcoming" or
    // "completed", even if its scheduled time has already passed.
    // The server decides the bucket (attendance_state); this tab does not
    // re-derive "completed" from the clock. Rows whose status is still
    // 'confirmed' land in No-show or Cancelled too, which is the whole point:
    // they used to sit under Completed.
    //   exactly one party joined → No-show
    //   neither party joined     → Cancelled, alongside real cancellations
    const isCancelled =
      item.status === 'cancelled' || isUnattendedState(item.attendance_state)
    const isNoShow = !isCancelled && isNoShowState(item.attendance_state)
    const isCompleted =
      !isCancelled && !isNoShow && (hasEnded || item.status === 'completed')
    const isUpcoming = !isCancelled && !isNoShow && !isCompleted
    if (filter === 'upcoming') return isUpcoming
    if (filter === 'completed') return isCompleted
    if (filter === 'cancelled') return isCancelled
    if (filter === 'no_show') return isNoShow
    return true // 'all'
  })

  // booking id → the OPEN change request on it → show a status chip instead of
  // the request buttons (no duplicate requests).
  //
  // "Open" is pending OR approved. An approved request is not finished: the admin
  // has agreed but still has to apply the cancel/reschedule to the session, and
  // the server rejects a second open request on the same booking. Matching only
  // `pending` here put the buttons back the instant an admin approved, so the
  // next click failed with a conflict error.
  const openByBooking = new Map(
    changeRequests
      .filter((r) => r.status === 'pending' || r.status === 'approved')
      .map((r) => [r.booking_id, r] as const),
  )

  if (loading) return <div className="loading-screen">Loading dashboard...</div>

  if (error) {
    return (
      <div className="empty-state">
        <XCircle size={48} />
        <h3>{error}</h3>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">
          Welcome back{user?.name ? `, ${user.name}` : ''}
        </h1>
        <p className="page-subtitle">
          Manage your scheduled interviews and track session progress.
        </p>
      </div>

      {/* Stats */}
      <div className="stats-grid ih-stats-grid">
        <div className="stat-card">
          <div className="icon primary"><Calendar size={22} /></div>
          <div className="value">{stats.upcoming}</div>
          <div className="label">Upcoming</div>
        </div>
        <div className="stat-card">
          <div className="icon success"><CheckCircle size={22} /></div>
          <div className="value">{stats.completed}</div>
          <div className="label">Completed</div>
        </div>
        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => setFilter('cancelled')}>
          <div className="icon danger"><XCircle size={22} /></div>
          <div className="value">{stats.cancelled}</div>
          <div className="label">Cancelled</div>
        </div>
        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => setFilter('no_show')}>
          <div className="icon danger"><Ban size={22} /></div>
          <div className="value">{stats.no_show}</div>
          <div className="label">No-show</div>
        </div>
        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => navigate('/interviewer/evaluations')}>
          <div className="icon warning"><ClipboardList size={22} /></div>
          <div className="value">{stats.pending_evaluations}</div>
          <div className="label">Pending Eval</div>
        </div>
      </div>

      {/* Interview List */}
      <div style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-dark)', marginBottom: 2 }}>Scheduled Interviews</h3>
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>All confirmed bookings with your clients</p>
          </div>
          <div className="ih-filter-tabs" style={{ display: 'flex', gap: 6, background: '#f1f5f9', borderRadius: 10, padding: 4 }}>
            {(['all', 'upcoming', 'completed', 'cancelled', 'no_show'] as FilterType[]).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  padding: '6px 18px', fontSize: 13, fontWeight: 600, borderRadius: 8,
                  border: 'none', cursor: 'pointer',
                  background: filter === f ? 'white' : 'transparent',
                  color: filter === f ? 'var(--text-dark)' : 'var(--text-muted)',
                  boxShadow: filter === f ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.15s',
                }}
              >
                {f === 'all' ? 'All' : f === 'upcoming' ? 'Upcoming' : f === 'completed' ? 'Completed' : f === 'cancelled' ? 'Cancelled' : 'No-show'}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="card" style={{ padding: '48px 20px', textAlign: 'center' }}>
            <div style={{ color: 'var(--text-muted)', marginBottom: 12 }}><Calendar size={40} /></div>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-dark)', marginBottom: 6 }}>
              {filter === 'cancelled' ? 'No cancelled interviews' : 'No interviews found'}
            </h3>
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {filter === 'cancelled'
                ? 'Sessions cancelled by a client — or rescheduled onto another interviewer — will appear here.'
                : 'Confirmed interviews will appear here once a client books a session.'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {filtered.map((item) => {
              const { date, time, hasEnded, isInProgress, isToday } = parseDate(item.scheduled_at, item.duration_minutes, interviewerTz)
              const isUnattended = isUnattendedState(item.attendance_state)
              const isCancelled = item.status === 'cancelled' || isUnattended
              const isNoShow = !isCancelled && isNoShowState(item.attendance_state)
              const isCompleted =
                !isCancelled && !isNoShow && (hasEnded || item.status === 'completed')
              const isDelivered = item.evaluation_status === 'delivered'
              const hasEvaluation = item.evaluation_id !== null
              // A no-show the CLIENT missed is still evaluable: the interviewer
              // was there, and their remarks ("candidate never joined", plus the
              // usual scores) are what the client and admin need on file. One
              // nobody joined is not — it reads as Cancelled.
              const canEvaluateNoShow = noShowAllowsEvaluation(
                item.attendance_state,
                item.no_show_party,
              )
              const displayName = item.client_name || item.client_email || 'Client'
              const subtitle = item.title || null

              let statusLabel: string
              let statusColor: string
              let statusBg: string
              let avatarBg: string

              if (isNoShow) {
                statusLabel = 'No-show'; statusColor = '#dc2626'; statusBg = '#fef2f2'; avatarBg = '#e5e7eb'
              } else if (isCancelled) {
                statusLabel = 'Cancelled'; statusColor = '#dc2626'; statusBg = '#fef2f2'; avatarBg = '#e5e7eb'
              } else if (isDelivered) {
                statusLabel = 'Delivered'; statusColor = '#7c3aed'; statusBg = '#f5f3ff'; avatarBg = '#9ca3af'
              } else if (isCompleted) {
                statusLabel = 'Completed'; statusColor = '#16a34a'; statusBg = '#f0fdf4'; avatarBg = '#9ca3af'
              } else if (isInProgress) {
                statusLabel = 'In Progress'; statusColor = '#ea580c'; statusBg = '#fff7ed'; avatarBg = '#ea580c'
              } else if (isToday) {
                statusLabel = 'Today'; statusColor = '#d97706'; statusBg = '#fffbeb'; avatarBg = 'var(--accent-gold)'
              } else {
                statusLabel = 'Upcoming'; statusColor = '#2563eb'; statusBg = '#eff6ff'; avatarBg = 'var(--primary-navy)'
              }
              const prepDone = prepDoneMap[item.id] ?? false

              return (
                <div
                  key={item.id}
                  className="ih-card"
                >
                  {/* Left: avatar + info */}
                  <div className="ih-card-main" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{
                      width: 46, height: 46, borderRadius: '50%', background: avatarBg,
                      color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 18, fontWeight: 700, flexShrink: 0,
                    }}>
                      {displayName.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-dark)', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {displayName}
                      </div>
                      {subtitle && (
                        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {subtitle}
                        </div>
                      )}
                      <div style={{
                        fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 5,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        <Clock size={11} style={{ flexShrink: 0 }} />
                        {date}, {time} · {item.duration_minutes} min
                      </div>
                      {isCancelled && (item.cancellation_reason || isUnattended) && (
                        <div style={{ fontSize: 12, color: '#dc2626', marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {item.cancellation_reason || UNATTENDED_REASON}
                        </div>
                      )}
                      {isNoShow && (
                        <div style={{ fontSize: 12, color: '#dc2626', marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {noShowWording(item.no_show_party)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right: status + actions */}
                  <div className="ih-card-actions">
                    <span
                      title={attendanceHint(item.attendance_state, item.no_show_party)}
                      style={{
                        fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 20,
                        background: statusBg, color: statusColor, whiteSpace: 'nowrap',
                        cursor: isNoShow || isUnattended ? 'help' : undefined,
                      }}
                    >
                      {statusLabel}
                    </span>

                    {/* Add to Calendar (.ics) — only for upcoming/today/in-progress
                        sessions. Built in the browser from this booking, so the
                        interviewer can download it any time before the session. */}
                    {!isCompleted && !isCancelled && !isNoShow && (
                      <button
                        className="btn btn-outline"
                        style={{ padding: '8px 14px', fontSize: 13, whiteSpace: 'nowrap' }}
                        title="Download an .ics calendar file for this session"
                        onClick={() => handleDownloadIcs(item)}
                      >
                        <CalendarPlus size={14} />
                        Add to Calendar
                      </button>
                    )}

                    {/* Completed / Delivered — and no-shows the CLIENT missed.
                        The interviewer was there for those, so they can still
                        record scores and remarks; the badge stays "No-show"
                        because an evaluation is not evidence of attendance
                        (see scheduling/attendance.py). */}
                    {(isCompleted || canEvaluateNoShow) && !isCancelled && (!hasEvaluation || item.evaluation_status === 'draft' || item.evaluation_status === 'interviewer_review') && (
                      <button
                        className="btn btn-primary"
                        style={{ padding: '8px 18px', fontSize: 13, whiteSpace: 'nowrap' }}
                        title={
                          canEvaluateNoShow
                            ? 'The candidate did not join. You can still record what happened and complete the evaluation.'
                            : undefined
                        }
                        onClick={() => openEvaluation(item)}
                      >
                        <ClipboardList size={14} />
                        {!hasEvaluation ? 'Add Evaluation' : 'Edit Evaluation'}
                      </button>
                    )}

                    {/* In Progress — show Start Interview + Add Evaluation */}
                    {isInProgress && !isCancelled && (
                      <>
                        <button
                          className="btn btn-primary"
                          style={{ padding: '8px 18px', fontSize: 13, whiteSpace: 'nowrap' }}
                          onClick={() => navigate(`/interviewer/bookings/${encodeId(item.id)}`, {
                            state: {
                              client_name: item.client_name,
                              client_email: item.client_email,
                              zoom_url: item.zoom_host_url ?? item.zoom_join_url,
                              zoom_password: item.zoom_password,
                              scheduled_at: item.scheduled_at,
                              duration_minutes: item.duration_minutes,
                              status: item.status,
                              target_role: item.target_role,
                            }
                          })}
                        >
                          <Video size={14} />
                          Start Interview
                        </button>
                        {(!hasEvaluation || item.evaluation_status === 'draft' || item.evaluation_status === 'interviewer_review') && (
                          <button
                            className="btn btn-outline"
                            style={{ padding: '8px 18px', fontSize: 13, whiteSpace: 'nowrap' }}
                            onClick={() => openEvaluation(item)}
                          >
                            <ClipboardList size={14} />
                            {!hasEvaluation ? 'Add Evaluation' : 'Edit Evaluation'}
                          </button>
                        )}
                      </>
                    )}

                    {/* Upcoming / Today — show Prep + Start Interview */}
                    {!isCompleted && !isInProgress && !isCancelled && !isNoShow && (
                      <>
                        {item.client_id && (
                          <button
                            className="btn btn-outline"
                            style={{ padding: '8px 16px', fontSize: 13, whiteSpace: 'nowrap' }}
                            onClick={() => navigate(`/interviewer/clients?prep=1&client=${item.client_id}&booking=${item.id}`)}
                          >
                            <ClipboardList size={14} />
                            Prep
                          </button>
                        )}
                        <button
                          className="btn btn-primary"
                          style={{ padding: '8px 18px', fontSize: 13, whiteSpace: 'nowrap', opacity: prepDone ? 1 : 0.5 }}
                          disabled={!prepDone}
                          title={!prepDone ? 'Complete prep first' : undefined}
                          onClick={() => navigate(`/interviewer/bookings/${encodeId(item.id)}`, {
                            state: {
                              client_name: item.client_name,
                              client_email: item.client_email,
                              zoom_url: item.zoom_host_url ?? item.zoom_join_url,
                              zoom_password: item.zoom_password,
                              scheduled_at: item.scheduled_at,
                              duration_minutes: item.duration_minutes,
                              status: item.status,
                              target_role: item.target_role,
                            }
                          })}
                        >
                          <Video size={14} />
                          Start Interview
                        </button>

                        {/* Interviewers cannot self-reschedule/cancel — they raise a
                            request for admin review. Show the pending chip once one
                            exists so they can't file duplicates. */}
                        {openByBooking.get(item.id) ? (
                          (() => {
                            const isApproved = openByBooking.get(item.id)!.status === 'approved'
                            return (
                              <span
                                title={
                                  isApproved
                                    ? 'Approved by an admin. The session stays on your schedule until they apply the change.'
                                    : 'Change requested — an admin is reviewing it. The session is unchanged until they approve.'
                                }
                                style={{
                                  fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 20,
                                  background: isApproved ? '#eff6ff' : '#fffbeb',
                                  color: isApproved ? '#1d4ed8' : '#d97706',
                                  whiteSpace: 'nowrap',
                                  border: `1px solid ${isApproved ? '#bfdbfe' : '#fde68a'}`,
                                  display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
                                }}
                              >
                                <Clock size={12} style={{ flexShrink: 0 }} />{' '}
                                {isApproved ? 'Approved — being applied' : 'Awaiting admin review'}
                              </span>
                            )
                          })()
                        ) : (
                          <>
                            <button
                              className="btn btn-outline"
                              style={{ padding: '8px 14px', fontSize: 13, whiteSpace: 'nowrap' }}
                              title="Request a reschedule (an admin will review it)"
                              onClick={() => { setCrModal({ item, type: 'reschedule' }); setCrReason('') }}
                            >
                              <RotateCcw size={14} />
                              Request Reschedule
                            </button>
                            <button
                              className="btn btn-outline"
                              style={{ padding: '8px 14px', fontSize: 13, whiteSpace: 'nowrap', color: 'var(--danger)', borderColor: 'var(--danger)' }}
                              title="Request a cancellation (an admin will review it)"
                              onClick={() => { setCrModal({ item, type: 'cancel' }); setCrReason('') }}
                            >
                              <Ban size={14} />
                              Request Cancel
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* ── Request Reschedule / Cancel modal ──────────────────────────────── */}
      {crModal && (() => {
        const info = parseDate(crModal.item.scheduled_at, crModal.item.duration_minutes, interviewerTz)
        const who = crModal.item.client_name || crModal.item.client_email || 'the client'
        const isCancel = crModal.type === 'cancel'
        return (
          <div className="modal-overlay" onClick={() => !crSubmitting && setCrModal(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
              <div className="modal-header">
                <h3 className="modal-title">{isCancel ? 'Request Cancellation' : 'Request Reschedule'}</h3>
                <button onClick={() => !crSubmitting && setCrModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <XCircle size={20} />
                </button>
              </div>
              <div className="modal-body">
                <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                  You can’t {isCancel ? 'cancel' : 'reschedule'} a booked session yourself. This sends a
                  request to the admin for review. <strong>{who}</strong> is notified only once the admin
                  {isCancel ? ' cancels' : ' reschedules'} the session.
                </p>
                <div style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--text-dark)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Clock size={13} /> {info.date}, {info.time} · {crModal.item.duration_minutes} min
                </div>
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label className="form-label">Reason <span style={{ color: 'var(--danger)' }}>*</span> (required)</label>
                  <textarea
                    className="form-input"
                    rows={3}
                    value={crReason}
                    onChange={(e) => setCrReason(e.target.value)}
                    placeholder={isCancel ? 'Why do you need to cancel?' : 'Why do you need to reschedule?'}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setCrModal(null)} disabled={crSubmitting}>
                  Close
                </button>
                <button type="button" className="btn btn-primary" onClick={submitChangeRequest} disabled={crSubmitting || !crReason.trim()}>
                  {crSubmitting ? 'Sending…' : 'Send Request'}
                </button>
              </div>
            </div>
          </div>
        )
      })()}

      <style>{`
        .ih-stats-grid { grid-template-columns: repeat(5, 1fr) !important; }
        @media (max-width: 1024px) { .ih-stats-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        /* Mobile: 2-column grid, tighter gap + padding so cards fit on 390px */
        @media (max-width: 480px) {
          .ih-stats-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 10px !important; }
          .ih-stats-grid .stat-card { padding: 14px 12px !important; }
          .ih-stats-grid .stat-card .value { font-size: 26px !important; }
          .ih-stats-grid .stat-card .icon { width: 36px !important; height: 36px !important; margin-bottom: 10px !important; }
        }

        .ih-card {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px 16px;
          background: white;
          border: 1px solid var(--border);
          border-radius: 14px;
          padding: 16px 20px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.05);
          transition: box-shadow 0.15s;
        }
        .ih-card:hover {
          box-shadow: 0 4px 16px rgba(0,0,0,0.09);
        }
        /* Filter tabs: scroll sideways rather than clipping the last tab on
           narrow screens. */
        .ih-filter-tabs {
          max-width: 100%;
          overflow-x: auto;
          scrollbar-width: none;
        }
        .ih-filter-tabs::-webkit-scrollbar { display: none; }
        .ih-filter-tabs button { flex-shrink: 0; }
        @media (max-width: 480px) {
          .ih-filter-tabs button { padding: 6px 14px !important; }
        }

        /* Client info: shrinkable, but never crushed narrower than 260px —
           below that the actions wrap onto their own row instead. */
        .ih-card-main {
          flex: 1 1 260px;
          min-width: 0;
        }
        /* Actions: wrap into rows, stay right-aligned, never squeeze the info */
        .ih-card-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          flex-wrap: wrap;
          gap: 8px 10px;
          margin-left: auto;
        }
        /* Once the actions have wrapped below the info, left-align them so they
           don't float oddly against the right edge. */
        @media (max-width: 900px) {
          .ih-card-actions {
            justify-content: flex-start;
            margin-left: 0;
            width: 100%;
          }
        }
        @media (max-width: 640px) {
          .ih-card {
            flex-direction: column;
            align-items: flex-start;
          }
          .ih-card-main { flex: 1 1 auto; width: 100%; }
        }
      `}</style>
    </div>
  )
}
